import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { notification } from 'antd';
import { optionDisplay, optionScore, parsePromptSlots } from '../utils/questionnaire-responses';
import { slateToPlainText } from '../utils/slate-plain-text';

const JSON_HEADERS = { headers: { 'Content-Type': 'application/json' } };

// The API answers refusals (422 incomplete, 409 no active test) with a
// { message } body; anything else falls back to the transport error.
const errorMessage = (error) => error?.response?.data?.message || error?.message || 'Unknown error';

// Map a response the server stored for `item` back to the value this page
// tracks locally (the option's display string, exactly as QuestionnaireItem
// derives it) and the value it sends (the option's score), so a reloaded page
// can resume where the respondent left off (PIN-221).
//
// The server keeps only the score, so options sharing one (RDRA's "No" and
// "Don't Know" are both 0) cannot be told apart: return null to leave the item
// unanswered and let the respondent pick again rather than redraw a guess.
const restoredResponse = (item, stored) => {
  const matches = (item.allowedResponses || []).filter((o) => String(optionScore(o)) === stored);
  if (matches.length > 1) return null;
  if (matches.length === 0) return { display: stored, value: stored };
  return { display: optionDisplay(matches[0]), value: optionScore(matches[0]) };
};

// Whether `item` has its answer, asking `filled(key)` about each response key it
// needs: every slot's composite key `<key>#<i>` for a multi-slot text item (a
// prompt with 3+ underscore runs, PIN-152 #5), otherwise its one key.
const isItemAnswered = (item, document, filled) => {
  const key = item.responseKey || `response_${item._id}`;
  if (item.responseType === 'text') {
    const slots = parsePromptSlots(slateToPlainText(document));
    if (Array.isArray(slots)) return slots.every((slot) => filled(`${key}#${slot.index}`));
  }
  return filled(key);
};

export const useQuestionnaireSession = (sessionID, options = {}) => {
  const {
    respondentNameRequired = false,
    requireTextResponses = false,
    successMessage = 'Questionnaire submitted',
    // Required by the server for a session an invite link created (PIN-324).
    raterKey
  } = options;

  const [items, setItems] = useState([]);
  const [documents, setDocuments] = useState({});
  const [responses, setResponses] = useState({});
  // Per-item guess flags, keyed by item id (PIN-179 #2). Kept separate from
  // `responses` because a guess annotates a response rather than being one —
  // it never gates submission.
  const [guesses, setGuesses] = useState({});
  const [headerData, setHeaderData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Why the questionnaire could not be loaded, once it has failed to.
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [respondentName, setRespondentName] = useState('');
  // The rater name the server already holds, for a resumed questionnaire.
  const [resumedRespondentName, setResumedRespondentName] = useState('');
  // Every response key the server held when the page loaded -- including the
  // answers `restoredResponse` could not redraw (PIN-324).
  const [storedKeys, setStoredKeys] = useState(() => new Set());
  const [saveError, setSaveError] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  // Saves go out one at a time, in order (PIN-221). `sentValues` remembers
  // every value sent, so Submit can resend the full set: a save that failed
  // on a flaky connection is repaired there instead of being lost.
  const saveQueue = useRef(Promise.resolve());
  const sentValues = useRef({});
  // Keys whose latest save failed; the banner shows only while one remains.
  const failedKeys = useRef(new Set());

  // Fetch all items on mount via session-based endpoint
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await axios.post(
          process.env.REACT_APP_API_URL + '/session/questionnaireItems',
          { sessionID },
          JSON_HEADERS
        );

        const itemList = response.data.items || [];
        setItems(itemList);

        // Extract Slate documents from item content
        const docs = {};
        itemList.forEach((item) => {
          if (item.content && item.content.examinee) {
            if (item.content.examinee.document) {
              docs[item._id] = item.content.examinee.document;
            }
          }
        });

        setDocuments(docs);

        // Redraw answers already stored on the record. The server keys them by
        // the part of the response key after `response_`, with multi-slot
        // answers under `<key>#<slot>` (PIN-152 #5).
        const stored = response.data.responses || {};
        const restored = {};
        const held = new Set();
        itemList.forEach((item) => {
          const key = item.responseKey || `response_${item._id}`;
          const serverKey = key.replace(/^response_/, '');
          Object.entries(stored).forEach(([storedKey, storedValue]) => {
            const [base, slot] = storedKey.split('#');
            if (base !== serverKey) return;
            const localKey = slot === undefined ? key : `${key}#${slot}`;
            held.add(localKey);
            const restoredValue = restoredResponse(item, storedValue);
            if (!restoredValue) return;
            restored[localKey] = restoredValue.display;
            sentValues.current[localKey] = restoredValue.value;
          });
        });
        setResponses((prev) => ({ ...restored, ...prev }));
        setStoredKeys(held);

        if (response.data.raterName) {
          setRespondentName(response.data.raterName);
          setResumedRespondentName(response.data.raterName);
        }

        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch items:', error);
        setLoadError(errorMessage(error));
        notification.error({
          message: 'Failed to load questionnaire',
          description: error.message
        });
        setLoading(false);
      }
    };

    fetchItems();
  }, [sessionID]);

  // Fetch header metadata on mount
  useEffect(() => {
    const fetchHeader = async () => {
      try {
        const response = await axios.get(
          process.env.REACT_APP_API_URL + '/session/' + sessionID + '/questionnaireHeader'
        );
        setHeaderData(response.data);
        const prefilledName = response.data?.formFields?.respondentName;
        // A name the respondent already saved (resumed) wins over the prefill.
        if (respondentNameRequired && prefilledName) {
          setRespondentName((prev) => prev || prefilledName);
        }
      } catch (error) {
        console.error('Failed to fetch questionnaire header:', error);
      }
    };
    fetchHeader();
  }, [sessionID, respondentNameRequired]);

  const postData = useCallback(
    (data) =>
      axios.post(
        process.env.REACT_APP_API_URL + '/session/setData',
        { sessionID, raterKey, data },
        JSON_HEADERS
      ),
    [sessionID, raterKey]
  );

  // Queue a save. A failure is shown to the respondent rather than only
  // logged; the queue itself never rejects, so later saves still go out.
  const saveData = useCallback(
    (data) => {
      Object.assign(sentValues.current, data);
      saveQueue.current = saveQueue.current
        .then(() => postData(data))
        .then(
          () => {
            Object.keys(data).forEach((key) => failedKeys.current.delete(key));
            if (failedKeys.current.size === 0) setSaveError(null);
          },
          (error) => {
            console.error('Save failed:', error);
            Object.keys(data).forEach((key) => failedKeys.current.add(key));
            setSaveError(errorMessage(error));
          }
        );
      return saveQueue.current;
    },
    [postData]
  );

  // Handle response changes from QuestionnaireItem radio buttons.
  // displayStr is stored in local state for UI selection tracking.
  // scoreValue (numeric) is sent to the backend; defaults to displayStr for plain-string options.
  // A cleared free-text answer is sent as null so the server deletes it: stored
  // as '', it would count toward the server's completeness check (PIN-221).
  const handleResponseChange = useCallback(
    (key, displayStr, scoreValue = displayStr) => {
      setResponses((prev) => ({ ...prev, [key]: displayStr }));
      const blank = typeof scoreValue === 'string' && scoreValue.trim() === '';
      saveData({ [key]: blank ? null : scoreValue });
    },
    [saveData]
  );

  // Flag or unflag an item as a guess (PIN-179 #2). The server clears the flag
  // on a null value; it also clears it on its own when a response is cleared.
  const handleGuessChange = useCallback(
    (itemId, guessed) => {
      setGuesses((prev) => ({ ...prev, [itemId]: guessed }));
      saveData({ [`guessed_${itemId}`]: guessed ? true : null });
    },
    [saveData]
  );

  // Handle data updates from Slate elements (inputs, functions, etc.)
  const handleUpdateData = useCallback(
    (newData) => {
      setResponses((prev) => ({ ...prev, ...newData }));
      saveData(newData);
    },
    [saveData]
  );

  // Handle respondent name changes.
  // Save under both keys: respondent_name (CPPS compat) and rater_fullName (RDRA report template).
  const handleParentNameChange = useCallback(
    (value) => {
      setRespondentName(value);
      saveData({ respondent_name: value, rater_fullName: value });
    },
    [saveData]
  );

  // Check that every answerable item has a non-empty response before allowing submit.
  // Choice items (allowedResponses) always gate submission. Free-text sentence-completion
  // items (responseType: 'text') gate only when `requireTextResponses` is set — the
  // examinee/parent form requires a response to every item (PIN-152 #6), while the examiner
  // flow leaves text items optional. Content-only items never gate. Whitespace-only
  // answers do not count.
  const answerableItems = useMemo(
    () =>
      items.filter(
        (item) =>
          (item.allowedResponses && item.allowedResponses.length > 0) ||
          (requireTextResponses && item.responseType === 'text')
      ),
    [items, requireTextResponses]
  );

  const allAnswered = useMemo(() => {
    const filled = (k) => String(responses[k] ?? '').trim().length > 0;
    return answerableItems.every((item) => isItemAnswered(item, documents[item._id], filled));
  }, [answerableItems, documents, responses]);

  // Per answerable item id, whether it has an answer -- here or on the server.
  // Unlike `allAnswered`, this counts an answer the page could not redraw: the
  // respondent is asked to pick that one again, but someone looking at what is
  // left needs to know it was answered (PIN-324).
  const answeredByItem = useMemo(() => {
    const answered = (k) => storedKeys.has(k) || String(responses[k] ?? '').trim().length > 0;
    return Object.fromEntries(
      answerableItems.map((item) => [item._id, isItemAnswered(item, documents[item._id], answered)])
    );
  }, [answerableItems, documents, responses, storedKeys]);

  const hasRespondentName = !headerData || respondentName.trim().length > 0;

  // Submit: wait for queued saves, resend every answer plus the rater name
  // (which also saves a prefilled name the respondent never edited), then end
  // the test. Two requests: the server holds ending a questionnaire to every
  // required item having a response, and refuses with a message otherwise.
  const handleSubmit = useCallback(
    async (onSubmitSuccess) => {
      setSubmitting(true);
      setSubmitError(null);

      try {
        await saveQueue.current;

        const answers = { ...sentValues.current };
        if (respondentName.trim()) {
          answers.respondent_name = respondentName;
          answers.rater_fullName = respondentName;
        }
        if (Object.keys(answers).length > 0) await postData(answers);
        failedKeys.current.clear();
        setSaveError(null);

        await postData({ sessionControl_endTest: true });

        notification.success({
          message: 'Questionnaire submitted',
          description: successMessage
        });

        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
      } catch (error) {
        console.error('Submit failed:', error);
        const message = errorMessage(error);
        setSubmitError(message);
        notification.error({
          message: 'Failed to submit',
          description: message
        });
        setSubmitting(false);
      }
    },
    [postData, respondentName, successMessage]
  );

  return {
    items,
    documents,
    responses,
    guesses,
    headerData,
    loading,
    loadError,
    submitting,
    respondentName,
    resumedRespondentName,
    saveError,
    submitError,
    allAnswered,
    answeredByItem,
    hasRespondentName,
    handleResponseChange,
    handleGuessChange,
    handleUpdateData,
    handleParentNameChange,
    handleSubmit,
    respondentNameRequired
  };
};
